import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/AnimatedSection/AnimatedSection';
import styles from './About.module.css';

const values = [
  {
    title: 'Radical Transparency',
    description:
      'Every experiment, every failure, every raw dataset — public from the moment it\'s collected. Not because transparency is easy, but because science without it is just storytelling.',
    icon: (
      <svg viewBox="0 0 36 36" fill="none">
        <circle cx="18" cy="18" r="10" stroke="currentColor" strokeWidth="2"/>
        <circle cx="18" cy="18" r="4" fill="currentColor" opacity="0.6"/>
        <path d="M4 18h4M28 18h4M18 4v4M18 28v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    color: '#52D68A',
  },
  {
    title: 'Community First',
    description:
      'Science is collaborative by nature — Labora just makes that collaboration visible. Every comment, follow, and collaboration request is a thread connecting researchers across institutions and borders.',
    icon: (
      <svg viewBox="0 0 36 36" fill="none">
        <circle cx="12" cy="14" r="4" stroke="currentColor" strokeWidth="2"/>
        <circle cx="24" cy="14" r="4" stroke="currentColor" strokeWidth="2"/>
        <path d="M6 28c0-3.3 2.7-6 6-6h12c3.3 0 6 2.7 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M18 10v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 2"/>
      </svg>
    ),
    color: '#38BDF8',
  },
  {
    title: 'Failure as Progress',
    description:
      'Every negative result, failed hypothesis, and scrapped approach contains information. Labora treats failed experiments as first-class research outputs — not embarrassments to be hidden.',
    icon: (
      <svg viewBox="0 0 36 36" fill="none">
        <path d="M18 8v4M18 24v4M8 18H4M32 18h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M11.5 11.5l-3-3M27.5 27.5l-3-3M24.5 11.5l3-3M8.5 27.5l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
        <circle cx="18" cy="18" r="5" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    color: '#A78BFA',
  },
  {
    title: 'Open Science',
    description:
      'Data, methods, and conclusions should be freely available to anyone curious enough to look. Open science accelerates discovery, reduces fraud, and makes science accessible beyond institutions.',
    icon: (
      <svg viewBox="0 0 36 36" fill="none">
        <rect x="6" y="10" width="24" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M13 6h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M12 19h12M12 23h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <circle cx="27" cy="9" r="4" fill="var(--accent-green)" opacity="0.9"/>
        <path d="M25.5 9h3M27 7.5v3" stroke="#0D0F14" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
    color: '#52D68A',
  },
];

const personas = [
  {
    title: 'Young independent researchers',
    description:
      'Like Aya, you started exploring science on your own terms. Labora is your first real home — a place where curiosity is the only credential you need.',
    emoji: '🔬',
    color: '#52D68A',
    traits: ['Independent curiosity', 'First experiments', 'Self-directed study'],
  },
  {
    title: 'Students doing real research',
    description:
      'Your school project deserves a real audience. Document your methodology, share your results, and connect with researchers who are working on the same questions.',
    emoji: '⚗️',
    color: '#38BDF8',
    traits: ['School & thesis projects', 'Undergraduate research', 'Science competitions'],
  },
  {
    title: 'Early-career scientists',
    description:
      'PhD students, postdocs, and researchers who are tired of keeping their work hidden until it\'s "perfect." Build a public record of your process — not just your conclusions.',
    emoji: '🌍',
    color: '#A78BFA',
    traits: ['PhD & grad research', 'Postdoctoral work', 'Cross-institution collaboration'],
  },
];

const About = () => (
  <main className={styles.main}>

    {/* ══════════════════════════════════════
        FOUNDER HERO — Aya's introduction
    ══════════════════════════════════════ */}
    <section className={styles.founderHero}>
      <div className={styles.founderGlow} aria-hidden="true" />
      <div className={styles.container}>
        <AnimatedSection className={styles.founderInner}>
          {/* Left: Aya's story */}
          <div className={styles.founderText}>
            <span className={styles.breadcrumb}>The story behind Labora</span>
            <h1 className={styles.founderHeadline}>
              Hi. I'm Aya.{' '}
              <span className={styles.titleAccent}>I built this because I needed it.</span>
            </h1>
            <p className={styles.founderIntro}>
              I'm 17. I've been passionate about science since I was nine years old. And for most of that time, I felt completely alone in it.
            </p>
          </div>

          {/* Right: Avatar */}
          <div className={styles.founderAvatarWrap} aria-hidden="true">
            <div className={styles.founderAvatar}>
              <span className={styles.founderInitials}>AK</span>
            </div>
            <div className={styles.founderAvatarLabel}>
              <span className={styles.founderName}>Aya Kobrosli</span>
              <span className={styles.founderTitle}>Founder & Young Scientist</span>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>

    {/* ══════════════════════════════════════
        STORY SECTION 1 — Early journey
    ══════════════════════════════════════ */}
    <section className={styles.storySection}>
      <div className={styles.container}>
        <AnimatedSection className={styles.storyBlock}>
          <div className={styles.storyLabel}>How it started</div>
          <div className={styles.storyContent}>
            <p>
              I was nine years old when I first got obsessed with biology. Not the textbook kind — the real kind. I used to spend hours reading about how cells communicate, how viruses mutate, how ecosystems balance themselves in ways that feel almost like intelligence. I filled notebooks with questions that nobody around me seemed to have answers to.
            </p>
            <p>
              By the time I was twelve, I had run my first real experiment — a controlled study on soil microbial activity across different composting conditions. It wasn't perfect. My methodology had gaps. My sample sizes were small. But the data was real, the questions were real, and the curiosity driving it was absolutely real.
            </p>
            <p>
              The problem was: where does a twelve-year-old go with real scientific curiosity and no institutional affiliation? You can read papers, but you can't ask questions of the people who wrote them. You can run experiments, but there's nowhere to publish them. You can care deeply about science, but the world largely treats that caring as a hobby until you're old enough to be taken seriously.
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>

    {/* ══════════════════════════════════════
        STORY SECTION 2 — The frustration
    ══════════════════════════════════════ */}
    <section className={styles.storySectionAlt}>
      <div className={styles.container}>
        <AnimatedSection className={styles.storyBlock}>
          <div className={styles.storyLabel}>The frustration</div>
          <div className={styles.storyContent}>
            <p>
              The deeper I got into science, the more I noticed how closed it all was. Research happened behind university walls. Results got published in journals that cost hundreds of dollars to access. The people doing the most interesting work were largely invisible until they had a paper in a prestigious journal — and that could take years.
            </p>
            <p>
              I kept thinking: what if someone is working on the exact same question I am, right now, and neither of us knows it? What if there's a researcher somewhere who has already made the mistake I'm about to make, and they have no way to share that with me?
            </p>
            <p>
              I also noticed something else — something that bothered me more than the paywalls and the institutional gatekeeping. The way science was presented to young people made it seem like a finished thing. A body of known facts. Not a messy, uncertain, collaborative process carried out by people who are frequently confused and often wrong.
            </p>
            <p>
              That gap between how science actually works and how it's presented felt like a kind of lie. And I was tired of it.
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>

    {/* ══════════════════════════════════════
        QUOTE PULL
    ══════════════════════════════════════ */}
    <section className={styles.quoteSection}>
      <div className={styles.container}>
        <AnimatedSection className={styles.quoteCard}>
          <div className={styles.quoteMark} aria-hidden="true">"</div>
          <blockquote className={styles.quoteText}>
            Science isn't a collection of facts. It's a way of being honest about what you don't know yet. I wanted to build a place that celebrates that honesty — especially for young people who are just beginning to figure out what they're capable of.
          </blockquote>
          <div className={styles.quoteAuthor}>
            <div className={styles.quoteAvatar}>AK</div>
            <div>
              <div className={styles.quoteAuthorName}>Aya Kobrosli</div>
              <div className={styles.quoteAuthorTitle}>Founder, Labora</div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>

    {/* ══════════════════════════════════════
        STORY SECTION 3 — Building Labora
    ══════════════════════════════════════ */}
    <section className={styles.storySection}>
      <div className={styles.container}>
        <AnimatedSection className={styles.storyBlock}>
          <div className={styles.storyLabel}>The moment I decided to build something</div>
          <div className={styles.storyContent}>
            <p>
              I was fifteen when I decided to actually do something about it. I had just finished a months-long project on antibiotic resistance patterns in soil bacteria — my most rigorous work to date. And when I was done, I had no idea what to do with it. There was no place to put it. No community to share it with. No way to find out if anyone else cared about the same question.
            </p>
            <p>
              I started sketching out what a platform for young scientists could look like. Not a science fair website. Not another educational tool designed by adults for students. Something real — a place where young researchers could document their work the way it actually happens: in progress, uncertain, honest.
            </p>
            <p>
              Labora is that platform. It's built on a simple belief: science is better when it's shared. Not just the finished, polished version — but the whole thing. The hypotheses that turned out to be wrong. The weeks when nothing worked. The unexpected results that opened up bigger questions. All of it matters, and all of it deserves an audience.
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>

    {/* ══════════════════════════════════════
        VISION
    ══════════════════════════════════════ */}
    <section className={styles.storySectionAlt}>
      <div className={styles.container}>
        <AnimatedSection className={styles.storyBlock}>
          <div className={styles.storyLabel}>My vision for Labora</div>
          <div className={styles.storyContent}>
            <p>
              I want Labora to be the place where the next generation of scientists comes to feel like they belong to science — before they have a degree, before they have institutional affiliation, before anyone has formally told them they're allowed.
            </p>
            <p>
              I want a fifteen-year-old in a small town to be able to post her first experiment and get a comment from a researcher at a university who sees something interesting in her data. I want the failures to be celebrated as much as the breakthroughs. I want science to feel like what it actually is: a deeply human process of trying to understand a world that is always more complicated than your best model of it.
            </p>
            <p>
              We're at the very beginning of this. But I believe that a more open, more honest, more accessible science — one that starts with young people and doesn't wait for them to grow up before taking their curiosity seriously — is worth building. So I'm building it.
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>

    {/* ══════════════════════════════════════
        CORE VALUES
    ══════════════════════════════════════ */}
    <section className={styles.valuesSection} id="values">
      <div className={styles.container}>
        <AnimatedSection className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>What Labora stands for</span>
          <h2 className={styles.sectionTitle}>Core values</h2>
          <p className={styles.sectionSub}>
            These aren't just principles — they're the reason Labora exists. Every feature decision comes back to them.
          </p>
        </AnimatedSection>

        <div className={styles.valuesGrid}>
          {values.map((value, i) => (
            <AnimatedSection key={value.title} delay={i * 70} className={styles.valueCard}>
              <div className={styles.valueIcon} style={{ color: value.color }}>
                {value.icon}
              </div>
              <h3 className={styles.valueTitle} style={{ color: value.color }}>
                {value.title}
              </h3>
              <p className={styles.valueDesc}>{value.description}</p>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>

    {/* ══════════════════════════════════════
        WHO IT'S FOR
    ══════════════════════════════════════ */}
    <section className={styles.personasSection}>
      <div className={styles.container}>
        <AnimatedSection className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>Who it's for</span>
          <h2 className={styles.sectionTitle}>Built for curious scientists at every stage</h2>
          <p className={styles.sectionSub}>
            Whether you're running your first experiment or deep into graduate research, Labora is built for you.
          </p>
        </AnimatedSection>

        <div className={styles.personasGrid}>
          {personas.map((persona, i) => (
            <AnimatedSection key={persona.title} delay={i * 80} className={styles.personaCard}>
              <div className={styles.personaEmoji}>{persona.emoji}</div>
              <h3 className={styles.personaTitle} style={{ color: persona.color }}>
                {persona.title}
              </h3>
              <p className={styles.personaDesc}>{persona.description}</p>
              <div className={styles.personaTraits}>
                {persona.traits.map((trait) => (
                  <span key={trait} className={styles.personaTrait}>{trait}</span>
                ))}
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>

    {/* ══════════════════════════════════════
        CLOSING CTA
    ══════════════════════════════════════ */}
    <section className={styles.ctaSection}>
      <div className={styles.ctaGlow} aria-hidden="true" />
      <div className={styles.container}>
        <AnimatedSection className={styles.ctaInner}>
          <span className={styles.ctaEyebrow}>Join the community</span>
          <h2 className={styles.ctaTitle}>
            Science is better<br />when it's shared.
          </h2>
          <p className={styles.ctaSub}>
            Start documenting your work today. Your curiosity deserves an audience from day one.
          </p>
          <div className={styles.ctaActions}>
            <Link to="/signup" className={styles.ctaPrimary}>
              Start Documenting
              <svg viewBox="0 0 20 20" fill="none">
                <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <Link to="/portfolio" className={styles.ctaSecondary}>
              Explore projects first
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>

  </main>
);

export default About;
